import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CircleNotch, PaperPlaneTilt } from "@phosphor-icons/react";

export default function OrdenCompraDialog({ open, onOpenChange, producto }) {
  const [numero, setNumero] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [proveedorEmail, setProveedorEmail] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [precio, setPrecio] = useState("");
  const [condiciones, setCondiciones] = useState("30 días");
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !producto) return;
    const falta = Math.max(0, (producto.reorder_point || 0) - (producto.stock_total || 0));
    setCantidad(String(falta > 0 ? falta : producto.reorder_point || 0));
    setProveedor("");
    setProveedorEmail("");
    setPrecio("");
    setCondiciones("30 días");
    api.get("/proveedores").then((r) => setProveedores(r.data));
    api.get("/orden-compra/sugerir-numero").then((r) => setNumero(r.data.numero));
  }, [open, producto]);

  const selectProveedor = (id) => {
    const pr = proveedores.find((p) => p.id === id);
    if (!pr) return;
    setProveedor(pr.nombre || "");
    setProveedorEmail(pr.email || "");
    if (pr.condiciones) setCondiciones(pr.condiciones);
  };

  if (!producto) return null;

  const total = (parseFloat(cantidad) || 0) * (parseFloat(precio) || 0);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/test-email", {
        recipient_email: proveedorEmail,
        template: "orden_compra",
        data: {
          numero,
          proveedor: proveedor || "Proveedor",
          fecha: new Date().toISOString().slice(0, 10),
          condiciones,
          items: [
            {
              descripcion: `${producto.nombre} (${producto.codigo})`,
              cantidad: parseInt(cantidad, 10) || 0,
              precio_unitario: parseFloat(precio) || 0,
            },
          ],
        },
      });
      toast.success(`Orden de compra ${numero} enviada a ${proveedorEmail}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al enviar la orden");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="orden-compra-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">Generar orden de compra</DialogTitle>
          <DialogDescription className="text-[#64748B]">
            {producto.nombre} ({producto.codigo}) · Stock {producto.stock_total} / reorden {producto.reorder_point}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">N° Orden</Label>
              <Input data-testid="oc-numero" value={numero} onChange={(e) => setNumero(e.target.value)} className="bg-white border-[#E2E8F0]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Condiciones</Label>
              <Input data-testid="oc-condiciones" value={condiciones} onChange={(e) => setCondiciones(e.target.value)} className="bg-white border-[#E2E8F0]" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Proveedor (agenda)</Label>
            {proveedores.length > 0 && (
              <Select onValueChange={selectProveedor}>
                <SelectTrigger data-testid="oc-proveedor-select" className="bg-white border-[#E2E8F0]">
                  <SelectValue placeholder="Autocompletar desde la agenda..." />
                </SelectTrigger>
                <SelectContent>
                  {proveedores.map((pr) => (
                    <SelectItem key={pr.id} value={pr.id} data-testid={`oc-proveedor-option-${pr.id}`}>
                      {pr.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Input data-testid="oc-proveedor" value={proveedor} onChange={(e) => setProveedor(e.target.value)} required placeholder="Plásticos del Pacífico" className="bg-white border-[#E2E8F0]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Email del proveedor</Label>
            <Input type="email" data-testid="oc-email" value={proveedorEmail} onChange={(e) => setProveedorEmail(e.target.value)} required placeholder="compras@proveedor.cl" className="bg-white border-[#E2E8F0]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Cantidad (faltante)</Label>
              <Input type="number" min="1" data-testid="oc-cantidad" value={cantidad} onChange={(e) => setCantidad(e.target.value)} required className="bg-white border-[#E2E8F0]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Precio unitario</Label>
              <Input type="number" min="0" step="0.01" data-testid="oc-precio" value={precio} onChange={(e) => setPrecio(e.target.value)} placeholder="0" className="bg-white border-[#E2E8F0]" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md bg-[#F8F9FA] border border-[#E2E8F0] px-4 py-3">
            <span className="text-sm text-[#64748B]">Total estimado</span>
            <span className="font-heading font-bold text-[#0F172A]" data-testid="oc-total">{fmtMoney(total)}</span>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="oc-cancel">Cancelar</Button>
            <Button type="submit" disabled={loading} data-testid="oc-submit" className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
              {loading ? <CircleNotch size={18} className="animate-spin mr-1.5" /> : <PaperPlaneTilt size={18} className="mr-1.5" />}
              Enviar orden
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
